import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import { Sales, CreateSalesRequest, SalesOptions } from 'types/sales';

/**
 * Sales API service
 * Consolidates all sales-related API calls in a single location
 */
export function useSalesService() {
  const apiClient = useApiClient();

  /**
   * Get sales options (customers, payment methods, products)
   */
  const getSalesOptions = useCallback(async (): Promise<SalesOptions> => {
    return apiClient.get<SalesOptions>(`${API_ROUTES.SALES}/options`);
  }, [apiClient]);

  /**
   * Get all sales
   * @param includeVoided - Optional flag to include voided sales (default: false)
   */
  const getAllSales = useCallback(
    async (includeVoided = false): Promise<Sales[]> => {
      const queryParams: Record<string, string> = {};
      if (includeVoided) {
        queryParams.includeVoided = 'true';
      }
      return apiClient.get<Sales[]>(API_ROUTES.SALES, queryParams);
    },
    [apiClient],
  );

  /**
   * Get a single sale by ID
   */
  const getSalesById = useCallback(
    async (id: string): Promise<Sales> => {
      return apiClient.get<Sales>(`${API_ROUTES.SALES}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new sale
   */
  const createSales = useCallback(
    async (sales: CreateSalesRequest): Promise<Sales> => {
      return apiClient.post<Sales>(
        API_ROUTES.SALES,
        sales as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Void a sale
   */
  const voidSale = useCallback(
    async (id: string, reason?: string): Promise<Sales> => {
      const body = reason ? { reason } : undefined;
      const result = await apiClient.delete<Sales>(
        `${API_ROUTES.SALES}/${id}`,
        body as unknown as Record<string, unknown>,
      );
      if (!result) {
        throw new Error('Failed to void sale: No response from server');
      }
      return result;
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getSalesOptions,
      getAllSales,
      getSalesById,
      createSales,
      voidSale,
    }),
    [getSalesOptions, getAllSales, getSalesById, createSales, voidSale],
  );
}
